import { Observable, of, defer, EMPTY } from "rxjs"
import { switchMap, filter, delayWhen, catchError } from "rxjs/operators"
import { ofType, StateObservable } from "redux-observable"
import type { EpicDependencies } from "../../../rxjs/configureEpics"
import { Router } from "../../../../server/router"
import {
    CLOSE_EMPLOYEE_FORM_CLICKED,
    EMPLOYEE_CREATE_SUCCESS,
    EMPLOYEE_DELETE_SUCCESS,
    EMPLOYEE_REQUEST_PASSWORD_RESET,
    EMPLOYEE_UPDATE_SUCCESS,
    EMPLOYEES_AGENDA_MOUNTED,
    employeesAgendaMounted,
} from "./actions"
import { waitForGlobalFilterSet } from "../../reports/customers/customersTable/epics"
import { setEmployeesDefaultFilter } from "../../../filters/actions"
import { ACCOUNT_SELECTED } from "../../../auth/actions"
import { isEmployeesAgendaOpened } from "../../../data/selectors"
import { createActionLoadingError } from "../../../data/actions"
import { getHttpHeaders } from "../../../auth/selectors"
import { NotificationType, openNotification } from "../../../uiKit/Notification/Notification"
import { createEventRequest, requestEvent } from "../../../../events/utils"
import { EventType } from "../../../../events/types"
import { EventDataResponse } from "../../../../graphql/types"
import { logger } from "../../../../utils/loggerWrap"
import { Action, StateObject } from "../../../redux/types"

export const employeesAgendaMountedEpics = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(EMPLOYEES_AGENDA_MOUNTED),
        delayWhen(action => waitForGlobalFilterSet(actions$, deps.getState(), action)),
        switchMap(() => [setEmployeesDefaultFilter()])
    )

export const fetchEmployeesAgendaOnAccountSwitch = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ACCOUNT_SELECTED),
        filter(() => isEmployeesAgendaOpened(deps.getState())),
        switchMap(() => [employeesAgendaMounted()])
    )

export const closeEmployeeFormEpics = (actions$: Observable<Action>) =>
    actions$.pipe(
        ofType(CLOSE_EMPLOYEE_FORM_CLICKED, EMPLOYEE_UPDATE_SUCCESS, EMPLOYEE_DELETE_SUCCESS, EMPLOYEE_CREATE_SUCCESS),
        switchMap(() => {
            Router.push("/employees/table")
            return EMPTY
        })
    )

export const employeePasswordResetRequestedEpics = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(EMPLOYEE_REQUEST_PASSWORD_RESET),
        switchMap((action: Action) => {
            logger.info("employeePasswordResetRequestedEpics - form submitted", {
                data: action.payload,
            })

            return defer(() =>
                requestEvent(
                    getHttpHeaders(deps.getState()),
                    createEventRequest(
                        EventType.PasswordResetRequested,
                        {
                            correlationId: deps.idgen.next(),
                        },
                        {
                            email: action.payload.email,
                            callbackUrl: location.origin + "/reset-password",
                        },
                        0
                    )
                )
            ).pipe(
                switchMap((res: EventDataResponse) => {
                    const id = res?.id
                    if (!id) {
                        throw new Error("employeePasswordResetRequestedEpics - Id is missing")
                    }
                    openNotification(NotificationType.Success, "Employee password successfully reset")
                    return []
                }),
                catchError(err => of(createActionLoadingError(err)))
            )
        })
    )

export default [
    employeePasswordResetRequestedEpics,
    employeesAgendaMountedEpics,
    fetchEmployeesAgendaOnAccountSwitch,
    closeEmployeeFormEpics,
]
